import { c as _c } from "react/compiler-runtime";
import React, { Fragment } from 'react';
import { AreaChart as RechartAreaChart, Area, XAxis, YAxis, ResponsiveContainer, type YAxisProps, Tooltip } from 'recharts';
import { type AreaChartProps, type AreaConfig, type CustomTooltipProps } from './types';
import { useTheme } from '@mui/material/styles';
import zIndexes from '../../theme/zIndex';
const AreaChart = t0 => {
  const $ = _c(76);
  let data;
  let getConfigByDataKey;
  let renderTooltip;
  let rest;
  let t1;
  let t2;
  let t3;
  let xAxis;
  let yAxis;
  if ($[0] !== t0) {
    ({
      containerWidth: t1,
      containerHeight: t2,
      data,
      getConfigByDataKey,
      renderTooltip,
      xAxis,
      yAxis,
      stacked: t3,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = data;
    $[2] = getConfigByDataKey;
    $[3] = renderTooltip;
    $[4] = rest;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = xAxis;
    $[9] = yAxis;
  } else {
    data = $[1];
    getConfigByDataKey = $[2];
    renderTooltip = $[3];
    rest = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    xAxis = $[8];
    yAxis = $[9];
  }
  const containerWidth = t1 === undefined ? "100%" : t1;
  const containerHeight = t2 === undefined ? "100%" : t2;
  const stacked = t3 === undefined ? false : t3;
  const theme = useTheme();
  let CustomTooltip;
  let T0;
  let T1;
  let defaultYAxisProps;
  let defaultYLabelProps;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[10] !== containerHeight || $[11] !== containerWidth || $[12] !== data || $[13] !== getConfigByDataKey || $[14] !== renderTooltip || $[15] !== rest || $[16] !== stacked || $[17] !== theme.tokens.background.brand.main.default || $[18] !== theme.tokens.border.main.tonal || $[19] !== theme.tokens.foreground.main.primary || $[20] !== theme.typography.fontWeightBold || $[21] !== xAxis) {
    const defaultAreaProps = {
      type: "monotone",
      lineWidth: 4,
      lineColor: theme.tokens.background.brand.main.default,
      areaColor: theme.tokens.background.brand.main.default
    };
    let t10;
    if ($[33] !== theme.tokens.border.main.tonal) {
      t10 = {
        stroke: theme.tokens.border.main.tonal
      };
      $[33] = theme.tokens.border.main.tonal;
      $[34] = t10;
    } else {
      t10 = $[34];
    }
    let t11;
    if ($[35] !== theme.typography.fontWeightBold) {
      t11 = {
        fontWeight: theme.typography.fontWeightBold,
        paddingTop: 10
      };
      $[35] = theme.typography.fontWeightBold;
      $[36] = t11;
    } else {
      t11 = $[36];
    }
    let t12;
    if ($[37] !== t10 || $[38] !== t11 || $[39] !== theme.tokens.foreground.main.primary) {
      t12 = {
        tickLine: false,
        axisLine: t10,
        tick: t11,
        stroke: theme.tokens.foreground.main.primary
      };
      $[37] = t10;
      $[38] = t11;
      $[39] = theme.tokens.foreground.main.primary;
      $[40] = t12;
    } else {
      t12 = $[40];
    }
    const defaultXAxisProps = t12;
    let t13;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = {
        textAnchor: "end"
      };
      $[41] = t13;
    } else {
      t13 = $[41];
    }
    let t14;
    if ($[42] !== theme.tokens.foreground.main.primary) {
      t14 = {
        orientation: "right",
        axisLine: false,
        tickLine: false,
        tick: t13,
        stroke: theme.tokens.foreground.main.primary
      };
      $[42] = theme.tokens.foreground.main.primary;
      $[43] = t14;
    } else {
      t14 = $[43];
    }
    defaultYAxisProps = t14;
    let t15;
    if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = {
        textAnchor: "end"
      };
      $[44] = t15;
    } else {
      t15 = $[44];
    }
    let t16;
    if ($[45] !== theme.tokens.foreground.main.primary || $[46] !== theme.typography.fontWeightBold) {
      t16 = {
        position: "top",
        offset: 15,
        fontWeight: theme.typography.fontWeightBold,
        fill: theme.tokens.foreground.main.primary,
        style: t15
      };
      $[45] = theme.tokens.foreground.main.primary;
      $[46] = theme.typography.fontWeightBold;
      $[47] = t16;
    } else {
      t16 = $[47];
    }
    defaultYLabelProps = t16;
    let t17;
    if ($[48] !== renderTooltip) {
      t17 = t18 => {
        const {
          active: t19,
          payload
        } = t18;
        const active = t19 === undefined ? false : t19;
        if (active && payload != null && payload?.length > 0 && renderTooltip != null) {
          return renderTooltip({
            dataKey: payload[0].dataKey,
            payload: payload[0].payload
          });
        }
        return null;
      };
      $[48] = renderTooltip;
      $[49] = t17;
    } else {
      t17 = $[49];
    }
    CustomTooltip = t17;
    const yDataKeys = Array.from(new Set(data.flatMap(_temp))).filter(_temp2);
    T1 = ResponsiveContainer;
    t8 = containerWidth;
    t9 = containerHeight;
    T0 = RechartAreaChart;
    t4 = data;
    t5 = rest;
    if ($[50] !== defaultXAxisProps || $[51] !== xAxis) {
      t6 = <XAxis dataKey="x" {...{
        ...defaultXAxisProps,
        ...xAxis
      }} />;
      $[50] = defaultXAxisProps;
      $[51] = xAxis;
      $[52] = t6;
    } else {
      t6 = $[52];
    }
    t7 = yDataKeys.map((dataKey, index) => {
      const areaWithDefaults = {
        ...defaultAreaProps,
        ...getConfigByDataKey?.(dataKey)
      };
      const {
        lineColor,
        lineWidth,
        areaColor,
        hasGradient: t18,
        ...areaProps
      } = areaWithDefaults;
      const hasGradient = t18 === undefined ? false : t18;
      return <Fragment key={index}>{hasGradient && <defs><linearGradient id={`gradient${index}`} x1="0" y1="0" x2="0" y2="1"><stop stopColor={areaColor} stopOpacity="0.54" /><stop offset="0.636016" stopColor={areaColor} stopOpacity="0.0978453" /><stop offset="1" stopColor={areaColor} stopOpacity="0" /></linearGradient></defs>}<Area stroke={lineColor} strokeWidth={lineWidth} dataKey={dataKey} fill={hasGradient ? `url(#gradient${index})` : areaColor} stackId={stacked ? "1" : undefined} {...areaProps} /></Fragment>;
    });
    $[10] = containerHeight;
    $[11] = containerWidth;
    $[12] = data;
    $[13] = getConfigByDataKey;
    $[14] = renderTooltip;
    $[15] = rest;
    $[16] = stacked;
    $[17] = theme.tokens.background.brand.main.default;
    $[18] = theme.tokens.border.main.tonal;
    $[19] = theme.tokens.foreground.main.primary;
    $[20] = theme.typography.fontWeightBold;
    $[21] = xAxis;
    $[22] = CustomTooltip;
    $[23] = T0;
    $[24] = T1;
    $[25] = defaultYAxisProps;
    $[26] = defaultYLabelProps;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
  } else {
    CustomTooltip = $[22];
    T0 = $[23];
    T1 = $[24];
    defaultYAxisProps = $[25];
    defaultYLabelProps = $[26];
    t4 = $[27];
    t5 = $[28];
    t6 = $[29];
    t7 = $[30];
    t8 = $[31];
    t9 = $[32];
  }
  const t10 = yAxis?.label;
  let t11;
  if ($[53] !== defaultYLabelProps || $[54] !== t10) {
    t11 = {
      ...defaultYLabelProps,
      ...t10
    };
    $[53] = defaultYLabelProps;
    $[54] = t10;
    $[55] = t11;
  } else {
    t11 = $[55];
  }
  let t12;
  if ($[56] !== defaultYAxisProps || $[57] !== t11 || $[58] !== yAxis) {
    t12 = <YAxis {...{
      ...defaultYAxisProps,
      ...yAxis,
      label: t11
    }} />;
    $[56] = defaultYAxisProps;
    $[57] = t11;
    $[58] = yAxis;
    $[59] = t12;
  } else {
    t12 = $[59];
  }
  let t13;
  if ($[60] !== CustomTooltip || $[61] !== renderTooltip) {
    t13 = renderTooltip != null && <Tooltip content={<CustomTooltip />} wrapperStyle={{
      zIndex: zIndexes.chartTooltip,
      pointerEvents: "none"
    }} allowEscapeViewBox={{
      x: true,
      y: true
    }} />;
    $[60] = CustomTooltip;
    $[61] = renderTooltip;
    $[62] = t13;
  } else {
    t13 = $[62];
  }
  let t14;
  if ($[63] !== T0 || $[64] !== t12 || $[65] !== t13 || $[66] !== t4 || $[67] !== t5 || $[68] !== t6 || $[69] !== t7) {
    t14 = <T0 data={t4} {...t5}>{t6}{t7}{t12}{t13}</T0>;
    $[63] = T0;
    $[64] = t12;
    $[65] = t13;
    $[66] = t4;
    $[67] = t5;
    $[68] = t6;
    $[69] = t7;
    $[70] = t14;
  } else {
    t14 = $[70];
  }
  let t15;
  if ($[71] !== T1 || $[72] !== t14 || $[73] !== t8 || $[74] !== t9) {
    t15 = <T1 width={t8} height={t9}>{t14}</T1>;
    $[71] = T1;
    $[72] = t14;
    $[73] = t8;
    $[74] = t9;
    $[75] = t15;
  } else {
    t15 = $[75];
  }
  return t15;
};
export default AreaChart;
export { AreaChart };
export type { AreaData } from './types';
function _temp(entry) {
  return Object.keys(entry);
}
function _temp2(key) {
  return key !== "x";
}